import React, { useState } from "react";
import { Box, Typography, Button, Checkbox, FormControlLabel } from "@mui/material";
import { parseAnswers } from "../utils/utils.ts";
import config from "../config/config.ts";
import { colors, fonts, shadows, radii, compactCardBreakpoint, shortViewportBreakpoint } from "../theme/tokens.ts";

const { BACK_API_KEY, BACK_URL } = config;

interface EmailFormProps {
  onSubmit: (email: string) => void;
  answers: { [key: string]: number };
}

const EMAIL_PATTERN = /\S+@\S+\.\S+/;

const inputSx = {
  width: "100%",
  padding: "9px 11px",
  borderRadius: radii.input,
  border: `1.5px solid ${colors.borderInput}`,
  fontSize: "12.5px",
  fontFamily: fonts.body,
  color: colors.body,
  outline: "none",
  "&::placeholder": { color: colors.faint },
  "&:focus": { borderColor: colors.coral },
};

//eslint-disable-next-line
const EmailForm: React.FC<EmailFormProps> = ({ onSubmit, answers }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [acceptsDataPolicy, setAcceptsDataPolicy] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const parsedAnswers = parseAnswers(answers);
  const emailValid = EMAIL_PATTERN.test(email);
  const formValid =
    emailValid &&
    fullName.trim() !== "" &&
    age.trim() !== "" &&
    gender !== "" &&
    country.trim() !== "" &&
    acceptsDataPolicy &&
    isAdult;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formValid) return;

    setIsLoading(true);

    try {
      const response = await fetch(`${BACK_URL}/quiz-submission/v2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apiKey": BACK_API_KEY
        },
        body: JSON.stringify({
          fullName,
          email,
          age,
          gender,
          country,
          acceptsDataPolicy,
          isAdult,
          answers: parsedAnswers,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        onSubmit(email);
      } else {
        throw new Error("Error al enviar el formulario");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error al enviar el formulario. Por favor, inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: 0,
        }}
      >
        <Box sx={{ maxWidth: 560, width: "100%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: colors.coral,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              color: "#ffffff",
            }}
          >
            ✓
          </Box>
          <Box>
            <Typography sx={{ fontFamily: fonts.heading, fontWeight: 800, fontSize: "19px", color: colors.teal }}>
              Resultados en camino!
            </Typography>
            <Typography sx={{ mt: "5px", fontFamily: fonts.body, fontSize: "12.5px", color: colors.muted }}>
              Enviamos tus resultados personalizados a {email}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: fonts.body, fontSize: "12px", color: colors.muted, lineHeight: 1.5 }}>
            Basado en tus respuestas, preparamos un material con tu perfil, tus
            fortalezas y recomendaciones para tu orientación.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "6px 0 0",
        // Mismo criterio que en landing y resultados: alto fijo y sin scroll
        // propio del documento (el embed real usa un iframe con
        // height:100vh sin auto-resize), para que un swipe no pueda
        // "escaparse" hacia la página de WordPress. Acá adentro, lo único
        // que scrollea es el propio formulario.
        //
        // OJO: sin "flex:1" a propósito. Con flex:1 (que fija flex-basis:0%)
        // el box terminaba tomando el alto de su contenido en vez de
        // 100vh cuando el contenido no entraba — exactamente al revés de lo
        // que se busca, y por eso se desbordaba en pantallas bajas (ej.
        // 1920x480). height:100vh ya alcanza, no hace falta que crezca
        // dentro de un contenedor padre que ni siquiera tiene alto fijo.
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: 560, width: "100%", height: "100%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", overflow: "hidden" }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontFamily: fonts.heading, fontWeight: 800, fontSize: "19px", color: colors.teal }}>
            ¡Excelente trabajo!
          </Typography>
          <Typography sx={{ mt: "5px", fontFamily: fonts.body, fontSize: "12.5px", color: colors.muted }}>
            Has completado todas las preguntas del test
          </Typography>
        </Box>

        {/*
          Wrapper transparente con el scroll: si el formulario no entra en el
          espacio disponible, scrollea acá adentro, acotado, sin escaparse
          hacia la página que embebe la app. En pantallas chicas (≤400x900)
          se le pone además un tope de alto para que quede más compacto.
        */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflowY: "auto",
            overscrollBehavior: "contain",
            [shortViewportBreakpoint]: {
              maxHeight: "40vh",
            },
            [compactCardBreakpoint]: {
              maxHeight: "60vh",
            },
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              background: "#ffffff",
              borderRadius: radii.card,
              padding: "18px",
              width: "100%",
              boxShadow: shadows.card,
              textAlign: "left",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Box>
              <Typography sx={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: "14px", color: colors.teal }}>
                Recibe tu resultados
              </Typography>
              <Typography sx={{ mt: "5px", fontFamily: fonts.body, fontSize: "12px", color: colors.muted, lineHeight: 1.5 }}>
                Basado en tus respuestas, preparamos un material con tu perfil,
                tus fortalezas y recomendaciones para tu orientación.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <Box
                component="input"
                type="text"
                placeholder="Nombre completo"
                value={fullName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
                sx={{ gridColumn: "1 / -1", ...inputSx }}
              />

              <Box
                component="input"
                type="email"
                placeholder="Tu dirección de email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                sx={{ gridColumn: "1 / -1", ...inputSx }}
              />

              <Box
                component="input"
                type="number"
                placeholder="Edad"
                value={age}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAge(e.target.value)}
                sx={inputSx}
              />

              <Box
                component="select"
                value={gender}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setGender(e.target.value)}
                sx={{ ...inputSx, color: gender ? colors.body : colors.faint }}
              >
                <option value="" disabled>Género</option>
                <option value="femenino">Femenino</option>
                <option value="masculino">Masculino</option>
                <option value="otro">Otro</option>
                <option value="prefiero-no-decir">Prefiero no decir</option>
              </Box>

              <Box
                component="input"
                type="text"
                placeholder="País"
                value={country}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCountry(e.target.value)}
                sx={{ gridColumn: "1 / -1", ...inputSx }}
              />
            </Box>

            <Typography sx={{ fontFamily: fonts.body, fontSize: "11px", color: colors.faint }}>
              Te enviaremos los resultados del test aquí
            </Typography>

            <FormControlLabel
              sx={{ ml: 0, alignItems: "flex-start" }}
              control={
                <Checkbox
                  checked={acceptsDataPolicy}
                  onChange={(e) => setAcceptsDataPolicy(e.target.checked)}
                  size="small"
                  sx={{ color: colors.borderInput, "&.Mui-checked": { color: colors.coral }, pt: 0 }}
                />
              }
              label={
                <Typography sx={{ fontFamily: fonts.body, fontSize: "11.5px", color: colors.muted }}>
                  He leído y acepto la{" "}
                  <a href="https://google.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.coral }}>
                    política de tratamiento de mis datos
                  </a>.
                </Typography>
              }
            />

            <FormControlLabel
              sx={{ ml: 0, alignItems: "flex-start" }}
              control={
                <Checkbox
                  checked={isAdult}
                  onChange={(e) => setIsAdult(e.target.checked)}
                  size="small"
                  sx={{ color: colors.borderInput, "&.Mui-checked": { color: colors.coral }, pt: 0 }}
                />
              }
              label={
                <Typography sx={{ fontFamily: fonts.body, fontSize: "11.5px", color: colors.muted }}>
                  Declaro ser mayor de 18 años
                </Typography>
              }
            />

            <Button
              type="submit"
              disableRipple
              disabled={!formValid || isLoading}
              sx={{
                background: colors.coral,
                color: "#ffffff",
                borderRadius: `${radii.pill}px`,
                padding: "10px 16px",
                fontFamily: fonts.heading,
                fontWeight: 600,
                fontSize: "12.5px",
                textTransform: "none",
                opacity: formValid ? 1 : 0.4,
                "&:hover": { background: colors.coralHover },
                "&.Mui-disabled": { color: "#ffffff", opacity: 0.4 },
              }}
            >
              {isLoading ? "Enviando..." : "Enviar resultados →"}
            </Button>

            <Typography sx={{ fontFamily: fonts.body, fontSize: "11px", fontStyle: "italic", color: colors.faint }}>
              Tu información es confidencial y solo será utilizada para
              enviarte tus resultados personalizado.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EmailForm;
